import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { TrendingUp, BookOpen, User, LogOut, Menu, X, LogIn, MessageSquare, Info, LineChart } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthPage = ['/login', '/register', '/forgot-password'].includes(location.pathname);
  const isAuthenticated = localStorage.getItem('token') !== null;
  const userName = localStorage.getItem('userName') || 'User';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('userCreatedAt');
    window.location.href = '/';
  };

  const navigateToProfile = () => {
    setShowProfileMenu(false);
    navigate('/profile');
  };

  useEffect(() => {
    if (!isAuthPage) {
      localStorage.setItem('lastVisitedPage', location.pathname);
    }
  }, [location.pathname, isAuthPage]);

  if (isAuthPage) {
    return null;
  }

  return (
    <nav className="bg-background-light shadow-soft sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0">
              <span className="text-2xl font-bold">
                <span className="text-white">Sarv</span>
                <span className="text-green-500">Kalyan</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <NavLink to="/premium-chart" icon={<LineChart size={18} />} text="Premium Chart" />
            <NavLink to="/paper-trading" icon={<TrendingUp size={18} />} text="Paper Trading" />
            <NavLink to="/trading-journal" icon={<BookOpen size={18} />} text="Trading Journal" />
            <NavLink to="/ai-chatbot" icon={<MessageSquare size={18} />} text="AI Chat" />
            <NavLink to="/about" icon={<Info size={18} />} text="About" />
            
            {isAuthenticated ? (
              <div className="relative ml-4">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 text-text-muted hover:text-secondary transition-colors bg-card px-4 py-2 rounded-lg"
                >
                  <User size={18} />
                  <span>{userName}</span>
                </button>
                
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-card ring-1 ring-black ring-opacity-5">
                    <div className="py-1" role="menu">
                      <button
                        onClick={navigateToProfile}
                        className="w-full text-left px-4 py-2 text-sm text-text hover:bg-background-light hover:text-secondary flex items-center"
                      >
                        <User size={16} className="mr-2" />
                        Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-text hover:bg-background-light hover:text-accent flex items-center"
                      >
                        <LogOut size={16} className="mr-2" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 bg-secondary text-background px-6 py-2 rounded-lg hover:bg-secondary-dark transition-colors"
              >
                <LogIn size={18} />
                <span>Login</span>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-text-muted hover:text-white hover:bg-background focus:outline-none"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <MobileNavLink to="/premium-chart" icon={<LineChart size={18} />} text="Premium Chart" />
            <MobileNavLink to="/paper-trading" icon={<TrendingUp size={18} />} text="Paper Trading" />
            <MobileNavLink to="/trading-journal" icon={<BookOpen size={18} />} text="Trading Journal" />
            <MobileNavLink to="/ai-chatbot" icon={<MessageSquare size={18} />} text="AI Chat" />
            <MobileNavLink to="/about" icon={<Info size={18} />} text="About" />
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-text hover:bg-background-light hover:text-accent flex items-center"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 bg-secondary text-background px-4 py-2 rounded-lg hover:bg-secondary-dark transition-colors mx-2"
              >
                <LogIn size={18} />
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

const NavLink = ({ to, icon, text }: { to: string; icon: React.ReactNode; text: string }) => (
  <Link
    to={to}
    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-text-muted hover:text-white hover:bg-background transition-colors"
  >
    {icon}
    <span>{text}</span>
  </Link>
);

const MobileNavLink = ({ to, icon, text }: { to: string; icon: React.ReactNode; text: string }) => (
  <Link
    to={to}
    className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-text-muted hover:text-white hover:bg-background block"
  >
    {icon}
    <span>{text}</span>
  </Link>
);

export default Navbar;